import { Injectable } from '@angular/core';
import { SessionStorageService } from 'ngx-webstorage';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  constructor(private storageService: SessionStorageService) {
  }

  public downloadFile(url: string, filename?: string): void {
    // De opslag (/storage) draait op een ander subdomein dan de app, waardoor het
    // 'download'-attribuut cross-origin genegeerd wordt en de browser de link in een
    // nieuw tabblad opent (popup-geblokkeerd). Met ?download dwingt de gateway een
    // Content-Disposition: attachment af, zodat het bestand echt gedownload wordt.
    const separator = url.indexOf('?') > -1 ? '&' : '?';
    const downloadUrl = `${url}${separator}download=${encodeURIComponent(filename ?? '')}`;
    const anchorEl = <HTMLAnchorElement>document.createElement('a');
    anchorEl.setAttribute('download', filename ?? '');
    anchorEl.setAttribute('href', downloadUrl);
    anchorEl.click();
    anchorEl.remove();
  }

  /**
   * Het bestand in de browser ophalen en als download aanbieden, in plaats
   * van de link naar de opslag te openen. Op een telefoon opende die link
   * een pagina van Backblaze ("Open met…"); nu komt op de iPhone het
   * deelvenster ("Bewaar video" / "Bewaar in Bestanden") en op een pc de
   * gewone download. Voor grote bestanden (hele wedstrijden) blijft de
   * gewone link beter: dit haalt het bestand eerst in het geheugen.
   */
  public async downloadAlsBestand(url: string, filename: string, opties: { deelvenster?: boolean } = {}): Promise<void> {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`download ${res.status}`);
    }
    const blob = await res.blob();
    const bestand = new File([blob], filename, { type: blob.type || 'video/mp4' });
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    const mobiel = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(navigator.userAgent));
    if (opties.deelvenster !== false && mobiel && typeof nav.share === 'function' && nav.canShare?.({ files: [bestand] })) {
      try {
        await nav.share({ files: [bestand], title: filename });
        return;
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') {
          return; // de gebruiker sloot het deelvenster
        }
        // anders: gewoon als download aanbieden
      }
    }
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  }

  socketDownloadInitiated(sid: string): void {
    this.storageService.store('downloads',  [...this.downloads, sid]);
  }

  public removeDownload(sid: string): void {
    const index = this.downloads.indexOf(sid);

    if (index > -1) {
      const downloads = this.downloads;
      downloads.splice(index, 1);
      this.storageService.store('downloads', downloads);
    }
  }

  get downloads(): string[] {
    return this.storageService.retrieve('downloads') ?? [];
  }

}
